import React, { useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MinusIcon, PlusIcon } from 'lucide-react';
import type { EngagementFact } from '../../data/scenarios';
interface CaseStudyToggleProps {
  label: string;
  facts: EngagementFact[];
  body: string;
}

/** Case study proof line. Collapsed by default; opens the engagement facts and story in place, below the control. */
export function CaseStudyToggle({
  label,
  facts,
  body
}: CaseStudyToggleProps) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  const uid = useId();
  const buttonId = `${uid}-button`;
  const panelId = `${uid}-panel`;
  return <div className={`mt-10 border-t bg-vapor ${open ? 'border-ink' : 'border-ridge'}`} data-id="element-40" data-mp="2v84xyfqt6k9s">
      <h4 data-id="element-41" data-mp="3986awtkcli5m">
        <button id={buttonId} type="button" aria-expanded={open} aria-controls={open ? panelId : undefined} onClick={() => setOpen(o => !o)} className="group flex w-full items-start gap-5 px-5 py-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink md:px-6 md:py-6" data-id="element-42" data-mp="397wi6y7x4ww8">
          <span className="min-w-0 flex-1" data-id="element-43" data-mp="wf3b0bdzvcv2">
            <span className="block pt-[2px] font-mark text-[12px] uppercase leading-none tracking-label text-ridge" data-id="element-44" data-mp="webe8x8nv937">Case Study</span>
            <span className="mt-3 block text-[16px] font-medium leading-[1.5] text-ink md:text-[17px]" data-id="element-45" data-mp="15gawfsvewj7q">{label}</span>
          </span>
          <span aria-hidden="true" className={`flex h-11 w-11 shrink-0 items-center justify-center border border-ink transition-[background-color,color] duration-150 ease-quiet ${open ? 'bg-ink text-paper' : 'text-ink group-hover:bg-paper'}`} data-id="element-46" data-mp="1aecfidsu8ij7">
            {open ? <MinusIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-47" data-mp="1o83nviuu1wdc" /> : <PlusIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-48" data-mp="1t5m2npvj75t9" />}
          </span>
        </button>
      </h4>
      <AnimatePresence initial={false} data-id="element-49" data-mp="3p8qqkvjfn3yj">
        {open && <motion.div id={panelId} role="region" aria-labelledby={buttonId} key="panel" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: reduce ? 0 : 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-50" data-mp="3p6xjxg8tkjqj">
            <div className="px-5 pb-7 md:px-6 md:pb-8" data-id="element-51" data-mp="ctg29gm7cj95">
              <dl className="border-t border-fog" data-id="element-52" data-mp="7rqs036t09m4">
                {facts.map(f => <div key={f.label} className="grid gap-1 border-b border-fog py-3 sm:grid-cols-[168px_minmax(0,1fr)] sm:gap-6" data-id="element-53" data-mp="lr8icmzqzuxl">
                    <dt className="pt-[5px] font-mark text-[11px] uppercase leading-[1.4] tracking-label text-ridge" data-id="element-54" data-mp="2niv7ej02xfrf">{f.label}</dt>
                    <dd className="text-[15px] leading-[1.55] text-ink" data-id="element-55" data-mp="2imeg5fxl2la4">{f.value}</dd>
                  </div>)}
              </dl>
              <p className="mt-7 max-w-[62ch] border-l border-ink pl-5 text-[16px] leading-[1.7] text-ink" data-id="element-56" data-mp="131chvvomfddd">{body}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}